"use client";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useConsultation } from "../../../../context/ConsultationContext";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import ConsultationHeader from "../components/ConsultationHeader";
import styles from "../css/medical-social-history.module.css";
import {
  getConsultationFull,
} from "../../../../api/clinic/consultationApi.js";

export default function MedicalSocialHistory() {
  const navigate = useNavigate();
  const { consultationData, previousStep, nextStep, goToStep, updateConsultationData, getStepDraft, setStepDraft } = useConsultation();
  const draftKey = "medicalSocialHistory";

  // Set current step to 2 when component mounts
  useEffect(() => {
    goToStep(2);
  }, [goToStep]);

  const defaultFormData = {
    familyHistory: "",
    medicalHistory: "",
    surgicalHistory: "",
    immunityStatus: "",
    alcohol: "",
    caffeine: "",
    smoking: "",
    social: "",
    travel: "",
    contraception: "",
    porphyria: false,
    porphyriaType: "acute",
  };
  
  const [formData, setFormData] = useState(() => getStepDraft(draftKey, defaultFormData));

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const consultationId = consultationData?.consultationId;
    if (!consultationId) return;

    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        const full = await getConsultationFull(consultationId);
        if (cancelled) return;

        const hist = full?.consultation_history;
        const h = Array.isArray(hist) ? hist[0] : hist;
        if (!h) return;

        const existingDraft = getStepDraft(draftKey, null);
        if (existingDraft) return;

        const next = {
          ...defaultFormData,
          familyHistory: h.familyhistory ?? "",
          medicalHistory: h.medicalhistory ?? "",
          surgicalHistory: h.surgicalhistory ?? "",
          immunityStatus: h.immunitystatus ?? "",
          alcohol: h.alcohol ?? "",
          caffeine: h.caffeine ?? "",
          smoking: h.smoking ?? "",
          social: h.social ?? "",
          travel: h.travel ?? "",
          contraception: h.contraception ?? "",
          porphyria: !!h.porphyria,
          porphyriaType: h.porphriatype ?? defaultFormData.porphyriaType,
        };
        setFormData(next);
        setStepDraft(draftKey, next);
      } catch (e) {
        if (cancelled) return;
        setError(e?.response?.data?.message || e?.message || "Failed to load history");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [consultationData?.consultationId]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const next = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };
      setStepDraft(draftKey, next);
      return next;
    });
  };

  const handlePrevious = () => {
    previousStep();
    navigate("/customer/clinic/patient-overview");
  };

  const handleNext = async () => {
    try {
      setSaving(true);
      setError("");
      nextStep();
      navigate("/customer/clinic/vitals-observations");
    } catch (e) {
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <Navbar />
      <main className={styles.mainContent}>
        <div className={styles.container}>
          <ConsultationHeader 
            variant="step"
            currentStep={2}
            totalSteps={10}
            stepTitle="Medical & Social History"
            stepDetails="Step 2 of 10 · Maria van der Merwe · General Consultation"
            onPrevious={handlePrevious}
            onNext={handleNext}
            nextDisabled={saving}
          />

          {loading && <div className={styles.loader}>Loading...</div>}
          {!loading && error && <div className={styles.errorMessage}>{error}</div>}

          <div className={styles.formContainer}>
            {/* MEDICAL HISTORY SECTION */}
            <div className={styles.sectionCard}>
              <h3 className={styles.blueSubHeader}>MEDICAL HISTORY</h3>
              <div className={styles.compactGrid}>
                <div className={styles.formGroup}>
                  <label>Family History</label>
                  <textarea 
                    name="familyHistory"
                    value={formData.familyHistory}
                    onChange={handleInputChange}
                    placeholder="Relevant family medical history..." 
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Medical History</label>
                  <textarea 
                    name="medicalHistory"
                    value={formData.medicalHistory}
                    onChange={handleInputChange}
                    placeholder="Past medical conditions..." 
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Surgical History</label>
                  <input 
                    type="text" 
                    name="surgicalHistory"
                    value={formData.surgicalHistory}
                    onChange={handleInputChange}
                    placeholder="Previous surgeries..." 
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Immunity Status</label>
                  <input 
                    type="text" 
                    name="immunityStatus"
                    value={formData.immunityStatus}
                    onChange={handleInputChange}
                    placeholder="Vaccination status..." 
                  />
                </div>
              </div>
            </div>

            {/* LIFESTYLE SECTION */}
            <div className={styles.sectionCard}>
              <h3 className={styles.blueSubHeader}>LIFESTYLE / SOCIAL HISTORY</h3>
              <div className={styles.threeColGrid}>
                <div className={styles.formGroup}>
                  <label>Alcohol Consumption</label>
                  <select name="alcohol" value={formData.alcohol} onChange={handleInputChange}>
                    <option value="">Select..</option>
                    <option value="none">None</option>
                    <option value="occasional">Occasional</option>
                    <option value="frequent">Frequent</option>
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label>Caffeine Use</label>
                  <select name="caffeine" value={formData.caffeine} onChange={handleInputChange}>
                    <option value="">Select..</option>
                    <option value="low">Low</option>
                    <option value="moderate">Moderate</option>
                    <option value="high">High</option>
                  </select>
                </div>
                <div className={styles.formGroup}>
                  <label>Smoking</label>
                  <select name="smoking" value={formData.smoking} onChange={handleInputChange}>
                    <option value="">Select..</option>
                    <option value="non-smoker">Non-smoker</option>
                    <option value="former">Former smoker</option>
                    <option value="current">Current smoker</option>
                  </select>
                </div>
              </div>
              
              <div className={styles.threeColGrid}>
                <div className={styles.formGroup}>
                  <label>Social History</label>
                  <input 
                    type="text" 
                    name="social"
                    value={formData.social}
                    onChange={handleInputChange}
                    placeholder="Living conditions, occupation..." 
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Travel History</label>
                  <input 
                    type="text" 
                    name="travel"
                    value={formData.travel}
                    onChange={handleInputChange}
                    placeholder="Recent travel..." 
                  />
                </div>
                <div className={styles.formGroup}>
                  <label>Contraception Method</label>
                  <input 
                    type="text" 
                    name="contraception"
                    value={formData.contraception}
                    onChange={handleInputChange}
                    placeholder="Current method..." 
                  />
                </div>
              </div>

              {/* PORPHYRIA TOGGLE BAR */}
              <div className={styles.porphyriaBar}>
                <div className={styles.toggleItem}>
                  <label className={styles.switch}>
                    <input 
                      type="checkbox" 
                      name="porphyria"
                      checked={formData.porphyria} 
                      onChange={handleInputChange}
                    />
                    <span className={styles.slider}></span>
                  </label>
                  <span className={styles.boldLabel}>Porphyria</span>
                </div>

                {/* Conditional Radio Buttons */}
                {formData.porphyria && (
                  <div className={styles.radioGroup}>
                    <label className={styles.radioLabel}>
                      <input 
                        type="radio" 
                        name="porphyriaType" 
                        value="acute"
                        checked={formData.porphyriaType === 'acute'} 
                        onChange={handleInputChange}
                      />
                      <span className={styles.customRadio}></span> Acute
                    </label>
                    <label className={styles.radioLabel}>
                      <input 
                        type="radio" 
                        name="porphyriaType" 
                        value="cutaneous"
                        checked={formData.porphyriaType === 'cutaneous'} 
                        onChange={handleInputChange}
                      />
                      <span className={styles.customRadio}></span> Cutaneous
                    </label>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}